import React, { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getIn, useFormik } from "formik";
import * as yup from "yup";
import {
  Grid,
  Box,
  Typography,
  Stack,
  CircularProgress,
  Container,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";

export default function PremiumChoiceGroup({
  label,
  name,
  formik,
  options,
  multiple = false,
  placeholder = "",
}) {
  const currentValue = getIn(formik.values, name);
  const errorText = getIn(formik.errors, name);
  const touched = getIn(formik.touched, name);
  const isError = touched && Boolean(errorText);

  const handleSelect = (optionValue) => {
    formik.setFieldTouched(name, true, false); // Trigger error on change

    if (multiple) {
      const currentArray = Array.isArray(currentValue) ? currentValue : [];
      if (currentArray.includes(optionValue)) {
        formik.setFieldValue(
          name,
          currentArray.filter((val) => val !== optionValue),
        );
      } else {
        formik.setFieldValue(name, [...currentArray, optionValue]);
      }
    } else {
      formik.setFieldValue(name, optionValue);
    }
  };

  return (
    <Box sx={{ mb: 4, width: "100%" }}>
      <Typography
        sx={{
          color: isError ? "#ff1744" : "rgba(255,255,255,0.8)",
          fontSize: 14,
          fontWeight: 700,
          mb: 2,
          lineHeight: 1.6,
          transition: "color 0.3s ease",
        }}
      >
        {label}
        {/* Show required indicator */}
        {formik?.isRequired?.[name] && (
          <Typography component="span" sx={{ color: "#ff1744", mr: 0.5 }}>
            *
          </Typography>
        )}
      </Typography>

      {/* Show placeholder text if no options are selected */}
      {placeholder && !currentValue?.length && (
        <Typography
          sx={{
            color: "rgba(255, 255, 255, 0.3)",
            fontSize: 13,
            mb: 2,
            fontStyle: "italic",
          }}
        >
          {placeholder}
        </Typography>
      )}

      <Stack spacing={1.5} sx={{ width: "100%" }}>
        {options.map((opt, i) => {
          const isSelected = multiple
            ? Array.isArray(currentValue) && currentValue.includes(opt.value)
            : currentValue === opt.value;

          return (
            <Box
              key={i}
              onClick={() => handleSelect(opt.value)}
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                borderRadius: "12px",
                bgcolor: isSelected
                  ? "rgba(0, 255, 204, 0.03)"
                  : "rgba(10, 10, 10, 0.5)",
                border: "1px solid",
                borderColor: isSelected
                  ? "#00ffcc"
                  : isError
                    ? "rgba(255, 23, 68, 0.5)" // Soft red border on error
                    : "rgba(255, 255, 255, 0.08)",
                cursor: "pointer",
                transition: "all 0.25s ease",
                boxSizing: "border-box",
                "&:hover": {
                  bgcolor: isSelected
                    ? "rgba(0, 255, 204, 0.05)"
                    : "rgba(20, 20, 20, 0.8)",
                  borderColor: isSelected
                    ? "#00ffcc"
                    : isError
                      ? "#ff1744"
                      : "rgba(255, 255, 255, 0.2)",
                },
              }}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: multiple ? "4px" : "50%",
                  border: "2px solid",
                  borderColor: isSelected
                    ? "#00ffcc"
                    : isError
                      ? "rgba(255, 23, 68, 0.5)"
                      : "rgba(255, 255, 255, 0.3)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s ease",
                  flexShrink: 0,
                  bgcolor: isSelected && multiple ? "#00ffcc" : "transparent",
                }}
              >
                {isSelected && !multiple && (
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: "#00ffcc",
                    }}
                  />
                )}
                {isSelected && multiple && (
                  <Box
                    component="span"
                    sx={{ color: "#000", fontSize: 11, fontWeight: 900 }}
                  >
                    ✓
                  </Box>
                )}
              </Box>

              <Typography
                sx={{
                  color: isSelected ? "#fff" : "rgba(255, 255, 255, 0.7)",
                  fontSize: 14.5,
                  fontWeight: isSelected ? 600 : 400,
                  lineHeight: 1.6,
                  transition: "color 0.2s ease",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                }}
              >
                {opt.label}
              </Typography>
            </Box>
          );
        })}
      </Stack>
      {isError && (
        <Typography
          sx={{ color: "#ff1744", fontSize: 13, mt: 1.5, fontWeight: 500 }}
        >
          {errorText}
        </Typography>
      )}
    </Box>
  );
}
